import { ScheduleCheckType } from '@consigli/types';
import clsx from 'clsx';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegCheckCircle, FaRegTimesCircle } from 'react-icons/fa';
import { IoWarningOutline } from 'react-icons/io5';
import { MdCancel } from 'react-icons/md';

import {
  ComponentReference,
  ScheduleComponent,
} from '@/pages/schedule-check/components/component-card';

interface ScheduleContextSidebarProps {
  component: ScheduleComponent;
}

interface CategoryMeta {
  icon: ReactNode;
  labelKey: string;
  badgeClass: string;
}

// Mirrors the section icons/colours on the comparison page so the badge in the
// preview panel reads as the same category the finding card sits under.
const getCategoryMeta = (status: ScheduleCheckType): CategoryMeta => {
  switch (status) {
    case ScheduleCheckType.COMPLETE:
      return {
        icon: <FaRegCheckCircle className="text-green-500" size={14} />,
        labelKey: 'schedule-check-comparison.sections.complete',
        badgeClass: 'bg-green-100 text-green-700',
      };
    case ScheduleCheckType.PARTIAL:
      return {
        icon: <IoWarningOutline className="text-orange-500" size={14} />,
        labelKey: 'schedule-check-comparison.sections.missing-in-some',
        badgeClass: 'bg-orange-100 text-orange-700',
      };
    case ScheduleCheckType.MISSING:
      return {
        icon: <MdCancel className="text-red-500" size={14} />,
        labelKey: 'schedule-check-comparison.sections.missing-in-both',
        badgeClass: 'bg-red-100 text-red-700',
      };
  }
};

interface ReferenceRowProps {
  label: string;
  reference?: ComponentReference;
}

// One "found in" line: an available tick/cross plus the file the component was
// located in and its page/sheet, matching what the finding card surfaces per
// document.
const ReferenceRow: FC<ReferenceRowProps> = ({ label, reference }) => {
  const { t } = useTranslation();

  if (!reference) return null;

  // Spreadsheets have no pages: the Excel pane locates the finding by sheet +
  // row/col, so `page` (a passed-through `page_number`) is meaningless here.
  // Show the sheet name for spreadsheets and the real page for PDFs.
  const sheetName = reference.cellCoordinates?.sheetName;

  return (
    <div className="flex items-start gap-2">
      {reference.available ? (
        <FaRegCheckCircle className="mt-0.5 flex-shrink-0 text-green-600" size={14} />
      ) : (
        <FaRegTimesCircle className="mt-0.5 flex-shrink-0 text-red-500" size={14} />
      )}
      <div className="min-w-0">
        <div className="text-sm font-medium text-day-neutral-dark">{label}</div>
        {reference.fileName && (
          <div className="truncate text-xs text-day-neutral-subtle" title={reference.fileName}>
            {reference.fileName}
          </div>
        )}
        {sheetName ? (
          <div className="truncate text-xs text-day-neutral-subtle" title={sheetName}>
            {t('schedule-viewer.sidebar.sheet')}: {sheetName}
          </div>
        ) : (
          reference.page !== undefined && (
            <div className="text-xs text-day-neutral-subtle">
              {t('schedule-check-comparison.component-card.page')} {reference.page}
            </div>
          )
        )}
      </div>
    </div>
  );
};

// Context panel for the schedule-check document viewers: which component the
// finding belongs to, the category it falls under (complete / missing in some /
// missing in both) and where it was found. Rendered as the AECOM
// DocumentViewer's sidebar, the same way version-compare surfaces its finding
// context.
export const ScheduleContextSidebar: FC<ScheduleContextSidebarProps> = ({ component }) => {
  const { t } = useTranslation();
  const category = getCategoryMeta(component.status);

  return (
    <div className="flex flex-col gap-4 text-day-neutral-dark">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          {t('schedule-viewer.sidebar.component')}
        </span>
        <span className="break-words text-sm font-semibold">{component.name}</span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          {t('schedule-viewer.sidebar.category')}
        </span>
        <span
          className={clsx(
            'inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
            category.badgeClass,
          )}
        >
          {category.icon}
          {t(category.labelKey)}
        </span>
      </div>

      <div className="flex flex-col gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          {t('schedule-viewer.sidebar.found-in')}
        </span>
        <ReferenceRow
          label={t('schedule-check-comparison.component-card.schematic')}
          reference={component.schematic}
        />
        <ReferenceRow
          label={t('schedule-check-comparison.component-card.drawings')}
          reference={component.drawings}
        />
        <ReferenceRow
          label={t('schedule-viewer.sidebar.schedule')}
          reference={component.schedule}
        />
      </div>
    </div>
  );
};
